/**
 * WorkStoppageFormModal
 *
 * Declare or correct an arrêt de travail.
 *
 * The first field is the one that makes this feature what it is: an arrêt is picked from the
 * planning, not typed in. Choosing an absence fills the period from it, so the suivi and the
 * planning can never disagree about when someone was off. The period is the leave's own
 * `from` / `to` — nothing is stored on the arrêt — so correcting it here (a CPAM décompte,
 * per "modification manuelle des données calculées automatiquement") moves the leave in the
 * planning, with its half days (Matin / Midi / Soir) as in Congés.
 *
 * On an existing arrêt the leave is not offered again: re-pointing a declared, reimbursed
 * arrêt at a different absence is not an edit, it is a different arrêt.
 *
 * Declaring has two modes, shown as two tabs at the top of the dialog:
 *   - EXISTING — the leave is already in the planning (often a collab's request from Congés,
 *     in which case saving the suivi validates it);
 *   - NEW — the arrêt reached HR before the planning: collab, leave type and dates are given
 *     here, and the API creates the planning leave (validated) and the suivi together.
 * In EXISTING mode the dates are shown from the picked leave, read-only; on an edit they move
 * it. An arrêt whose leave was deleted from the planning has no period to show.
 * Justificatifs picked while declaring are held until the save returns the planning leave's
 * id, then uploaded onto it.
 *
 * Laid out in FormDialog rows — label on the left, input on the right — like the Congés form
 * and the permissions page's profile dialog.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box, Button, TextField, MenuItem, ListSubheader, Stack, Autocomplete, Switch, Typography, Alert,
  InputAdornment, Tooltip, Tabs, Tab,
} from "@mui/material";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import WorkStoppageOverlapDialog from "../../components/workStoppages/WorkStoppageOverlapDialog";
import Langs, { Localize } from "../../app/lang/langs";
import { showErrorToast, showSuccessToast, isCollabPresent } from "../../app/utils/helpers";
import NetworkManager from "../../app/managers/networkManager";
import {
  addWorkStoppage, editWorkStoppage, getEligibleLeaves, clearEligibleLeaves, REIMBURSEMENT_STATUSES,
  getWorkStoppage, getWorkStoppageAttachments, uploadWorkStoppageAttachment,
  deleteWorkStoppageAttachment, clearWorkStoppageAttachments,
} from "../../components/workStoppages/workStoppagesSlice";
import { StatusOption } from "../../components/workStoppages/WorkStoppageStatus";
import WorkStoppageAttachments from "../../components/workStoppages/WorkStoppageAttachments";
import FormDialog, { FormRow, FormSection, FormButton, formFieldSx } from "../../components/popups/FormDialog";
import LeaveDateRangeField, {
  isLeavePeriodValid, leavePeriodFromTimestamps,
} from "../../components/inputs/LeaveDateRangeField";
import LeaveTypeSelect from "../../components/inputs/LeaveTypeSelect";
import FormDatePicker from "../../components/inputs/FormDatePicker";

/** `YYYY-MM-DD` is what the API stores and what FormDatePicker speaks. */
const toInputDate = (value) => (value ? moment(value).format("YYYY-MM-DD") : "");

/** The server-computed figures of a row, as the form's inputs hold them. */
const computedFigures = (row) => ({
  cpam_days: row.cpam_days ?? "",
  maintenance_days_100: row.maintenance_days_100 ?? "",
  maintenance_days_80: row.maintenance_days_80 ?? "",
  maintenance_days_unpaid: row.maintenance_days_unpaid ?? "",
});

const MAINTENANCE_FIELDS = ["maintenance_days_100", "maintenance_days_80", "maintenance_days_unpaid"];

/** Whether a référentiel line is indemnised by the CPAM (its `ijss` names an IJSS). */
const motifHasIjss = (motif) => /^ijss/i.test(String(motif?.ijss || "").trim());

/** The motif select's value for "another motif", typed in `custom_motif`. */
const CUSTOM_MOTIF = "__CUSTOM__";

/** The only motif whose therapeutic rate means anything. */
const THERAPEUTIC_MOTIF_CODE = "SAN_TEMPS_PARTIEL_THERAPEUTIQUE";

const normalizeLabel = (value) => String(value || "")
  .normalize("NFD").replace(/[\u0300-\u036f]/g, "").trim().toLowerCase();

/** An empty text field must clear the column, not send the string "". */
const orNull = (value) => (value === "" || typeof value === "undefined" ? null : value);

const emptyForm = {
  id_planning: null,
  // NEW mode only: who, and which leave type, the planning leave is created for.
  id_collab: null,
  id_sample_leave: "",
  id_absence_reference: "",
  // Typed when the référentiel has no line for the motif (id_absence_reference = CUSTOM_MOTIF).
  custom_motif: "",
  stoppage_status: "INITIAL",
  id_parent: null,
  start_date: "",
  end_date: "",
  // NEW mode only: the half days of the planning leave being created.
  start_period: "MORNING",
  end_period: "EVENING",
  received_at: "",
  declared_at: "",
  subrogation: null,
  salary_maintenance: null,
  cpam_days: "",
  therapeutic_rate: "",
  maintenance_days_100: "",
  maintenance_days_80: "",
  maintenance_days_unpaid: "",
  // Same defaults as the columns: the CPAM owes something on every arrêt, the prévoyance
  // only on long ones.
  cpam_reimbursement_status: "PENDING",
  prevoyance_reimbursement_status: "NOT_APPLICABLE",
  comments: "",
};

/**
 * @param {Object} props
 * @param {boolean} props.visible
 * @param {Function} props.onHide
 * @param {Object|null} props.stoppage - the row being edited, or null to declare a new one
 * @param {Array} props.list - the loaded arrêts, used to offer a parent for a prolongation
 * @param {Function} props.onSaved - called after a successful save, to refresh the table
 * @param {number|null} [props.preselectedLeaveId] - a pending request being validated: the
 *        form opens in EXISTING mode on that leave
 */
export default function WorkStoppageFormModal({ visible, onHide, stoppage, list, onSaved, preselectedLeaveId }) {
  const dispatch = useDispatch();
  const isMobile = useSelector((state) => state.settings.isMobile);
  const reference = useSelector((state) => state.workStoppages.reference);
  const eligibleLeaves = useSelector((state) => state.workStoppages.eligibleLeaves);
  const loadingLeaves = useSelector((state) => state.workStoppages.loadingEligibleLeaves);
  const attachments = useSelector((state) => state.workStoppages.attachments);
  const attachmentsLoading = useSelector((state) => state.workStoppages.attachmentsLoading);
  const allCollabsList = useSelector((state) => state.collabs.allCollabsList);
  const sampleLeaves = useSelector((state) => state.sampleLeaves.list);

  const isEdit = !!stoppage;
  // NEW first; validating a pending request is by definition about that existing leave.
  const [mode, setMode] = useState(preselectedLeaveId ? "EXISTING" : "NEW");
  const isNew = !isEdit && mode === "NEW";
  // Where the dates can be changed: they create the leave (NEW) or move it (an edit of an
  // arrêt that still has one). In EXISTING mode they are the picked leave's, shown only.
  const canEditPeriod = isNew || (isEdit && !!stoppage.id_planning);
  // Justificatifs picked before the arrêt exists; uploaded once the save returns its leave.
  const [pendingFiles, setPendingFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  // The preselected request is applied once per opening, when the eligible leaves arrive.
  const preselectApplied = useRef(false);
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  // Which figures are manual entries. Local rather than read off `stoppage`, because a
  // recalculation clears them while the dialog stays open on the same (now stale) row.
  const [overridden, setOverridden] = useState({ cpam: false, maintenance: false });
  const [recalculating, setRecalculating] = useState(false);
  // The computed figures as last loaded or recalculated. A figure still equal to its baseline
  // is not sent on save: the API reads any cpam_days / maintien value in the body as a manual
  // entry, so echoing the formula's own result back would silently freeze it as "manual".
  const [baseline, setBaseline] = useState({});

  useEffect(() => {
    if (!visible) return;
    setOverridden({
      cpam: !!stoppage?.cpam_days_overridden,
      maintenance: !!stoppage?.maintenance_overridden,
    });
    setBaseline(stoppage ? computedFigures(stoppage) : {});
    if (stoppage) {
      setForm({
        ...emptyForm,
        id_planning: stoppage.id_planning ?? null,
        id_absence_reference: stoppage.id_absence_reference ?? (stoppage.custom_motif ? CUSTOM_MOTIF : ""),
        custom_motif: stoppage.custom_motif || "",
        stoppage_status: stoppage.stoppage_status || "INITIAL",
        id_parent: stoppage.id_parent ?? null,
        // The period is the planning leave's, with its half days.
        ...(stoppage.planning_from && stoppage.planning_to
          ? leavePeriodFromTimestamps(stoppage.planning_from, stoppage.planning_to)
          : { start_date: "", end_date: "" }),
        received_at: toInputDate(stoppage.received_at),
        declared_at: toInputDate(stoppage.declared_at),
        subrogation: stoppage.subrogation,
        salary_maintenance: stoppage.salary_maintenance,
        cpam_days: stoppage.cpam_days ?? "",
        therapeutic_rate: stoppage.therapeutic_rate ?? "",
        maintenance_days_100: stoppage.maintenance_days_100 ?? "",
        maintenance_days_80: stoppage.maintenance_days_80 ?? "",
        maintenance_days_unpaid: stoppage.maintenance_days_unpaid ?? "",
        cpam_reimbursement_status: stoppage.cpam_reimbursement_status || emptyForm.cpam_reimbursement_status,
        prevoyance_reimbursement_status: stoppage.prevoyance_reimbursement_status || emptyForm.prevoyance_reimbursement_status,
        comments: stoppage.comments || "",
      });
    } else {
      setForm(emptyForm);
      // Only fetched for a new arrêt: it is a scan of the planning, and the answer changes
      // as soon as anything is declared, so it is never cached across openings.
      dispatch(getEligibleLeaves({}));
    }
    setErrors({});
    setMode(preselectedLeaveId ? "EXISTING" : "NEW");
    setPendingFiles([]);
    preselectApplied.current = false;
    // A declared arrêt's justificatifs live on its planning leave.
    if (stoppage && stoppage.id_planning) dispatch(getWorkStoppageAttachments(stoppage.id_planning));
    return () => {
      if (!stoppage) dispatch(clearEligibleLeaves());
      dispatch(clearWorkStoppageAttachments());
    };
  }, [visible, stoppage, preselectedLeaveId, dispatch]);

  /** Validating a pending request: open on that leave as soon as the list has it. */
  useEffect(() => {
    if (!visible || isEdit || !preselectedLeaveId || preselectApplied.current) return;
    const leave = (eligibleLeaves || []).find((row) => row.id === preselectedLeaveId);
    if (leave) {
      preselectApplied.current = true;
      onLeaveChange(leave);
    }
    // onLeaveChange only sets state; leaving it out keeps this from re-running every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, isEdit, preselectedLeaveId, eligibleLeaves]);

  /** The leave types a new arrêt can be created with — the work-stoppage ones. */
  const workStoppageLeaveTypes = useMemo(
    () => (sampleLeaves || [])
      .filter((sample) => sample.isWorkStoppage)
      .sort((a, b) => String(a.label).localeCompare(String(b.label))),
    [sampleLeaves]
  );

  /**
   * Collabs a new arrêt can be declared for: internes currently present. An externe's
   * absences are their own employer's to follow — the API refuses them too.
   */
  const collabOptions = useMemo(
    () => (allCollabsList || []).filter((collab) =>
      isCollabPresent(collab.statut) && String(collab.type_collab || "").toLowerCase() !== "externe"),
    [allCollabsList]
  );
  const selectedCollab = useMemo(
    () => collabOptions.find((collab) => collab.id === form.id_collab) || null,
    [collabOptions, form.id_collab]
  );

  const selectedLeave = useMemo(
    () => (eligibleLeaves || []).find((leave) => leave.id === form.id_planning) || null,
    [eligibleLeaves, form.id_planning]
  );

  const selectedMotif = useMemo(
    () => (reference || []).find((row) => row.id === form.id_absence_reference) || null,
    [reference, form.id_absence_reference]
  );

  /**
   * The référentiel category of the leave type — picked (NEW), of the picked leave (EXISTING),
   * or of the arrêt's leave (edit). Null when the type is not linked to one.
   */
  const leaveTypeCategory = isEdit
    ? stoppage.planning_reference_category || null
    : isNew
      ? (sampleLeaves || []).find((sample) => sample.id === form.id_sample_leave)?.referenceCategory || null
      : selectedLeave?.reference_category || null;

  /**
   * The motifs offered: only the leave type's category once a type is known (and linked to a
   * category that has motifs); every motif, grouped by category, before that. A motif already
   * on the arrêt stays offered even if it falls outside, so an edit never blanks it.
   */
  const motifGroups = useMemo(() => {
    const inCategory = leaveTypeCategory
      ? (reference || []).filter((row) => row.category === leaveTypeCategory)
      : [];
    const rows = inCategory.length ? [...inCategory] : [...(reference || [])];
    if (selectedMotif && !rows.some((row) => row.id === selectedMotif.id)) rows.push(selectedMotif);
    const groups = new Map();
    rows.forEach((row) => {
      const category = row.category || "—";
      if (!groups.has(category)) groups.set(category, []);
      groups.get(category).push(row);
    });
    return [...groups.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [reference, leaveTypeCategory, selectedMotif]);

  /**
   * Candidate parents for a prolongation: the initial arrêts of the SAME collab. The API
   * enforces this too — this only keeps the picker from offering what would be refused.
   */
  const parentOptions = useMemo(() => {
    const idCollab = isEdit ? stoppage.id_collab : (isNew ? form.id_collab : selectedLeave?.id_collab);
    if (!idCollab) return [];
    return (list || []).filter(
      (row) => row.id_collab === idCollab && !row.id_parent && row.id !== stoppage?.id
    );
  }, [list, isEdit, isNew, form.id_collab, stoppage, selectedLeave]);

  /**
   * The leave type, shown in every mode: chosen in NEW mode; read off the planning leave
   * otherwise, where it is the absence's own and changing it is a planning edit, not a suivi one.
   */
  const leaveTypeLabel = isEdit
    ? [stoppage.planning_type, stoppage.planning_leave_label].filter(Boolean).join(" - ")
    : (selectedLeave ? [selectedLeave.type, selectedLeave.leave_label].filter(Boolean).join(" - ") : "");

  const setField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  /** Picking the absence fills the period from it — that is the point of picking it. */
  const onLeaveChange = (leave) => {
    setForm((prev) => ({
      ...prev,
      id_planning: leave ? leave.id : null,
      ...(leave ? leavePeriodFromTimestamps(leave.from, leave.to) : {}),
    }));
    setErrors((prev) => ({ ...prev, id_planning: "", start_date: "" }));
    if (leave) suggestMotifFor(leave.reference_category, leave.leave_label);
  };

  /**
   * A leave type was picked: offer its category's motif. Kept if the motif already chosen is
   * in that category; otherwise the one named like the type ("Maladie" → Maladie), or the
   * category's only motif. With several and no name match, the motif is left for HR to pick.
   */
  const suggestMotifFor = (category, typeLabel) => {
    if (!category) return;
    const inCategory = (reference || []).filter((row) => row.category === category);
    if (!inCategory.length) return;
    // A motif already in the category, or one typed by hand, is HR's choice: kept.
    if (form.id_absence_reference === CUSTOM_MOTIF || inCategory.some((row) => row.id === form.id_absence_reference)) return;
    const motif = inCategory.find((row) => normalizeLabel(row.label) === normalizeLabel(typeLabel))
      || (inCategory.length === 1 ? inCategory[0] : null);
    if (motif) onMotifChange(motif.id);
    else setForm((prev) => ({ ...prev, id_absence_reference: "" }));
  };

  /** NEW mode: the leave type picked, and the motif suggested from its category. */
  const onLeaveTypeChange = (sample) => {
    setField("id_sample_leave", sample ? sample.id : "");
    if (sample) suggestMotifFor(sample.referenceCategory, sample.description);
  };

  /**
   * A motif was picked: the inputs take the référentiel's values for it — subrogation and
   * maintien de salaire from the Syntec grid, the CPAM reimbursement status from whether it
   * is indemnised at all, and no therapeutic rate outside temps partiel thérapeutique. They
   * all stay editable: a collab who does not meet the ancienneté condition is exactly the case
   * Paie corrects by hand. A reimbursement already under way is never reset.
   *
   * In NEW mode with no leave type yet, the type is picked from the motif's category.
   */
  const onMotifChange = (idReference) => {
    const motif = (reference || []).find((row) => row.id === idReference);
    setForm((prev) => {
      const next = { ...prev, id_absence_reference: idReference };
      // "Another motif" has no Syntec terms to apply: the inputs keep what HR set.
      if (!motif) return next;
      next.subrogation = motif.subrogation ? 1 : 0;
      next.salary_maintenance = /^oui/i.test(String(motif.salary_maintenance || "").trim()) ? 1 : 0;
      if (["PENDING", "NOT_APPLICABLE"].includes(prev.cpam_reimbursement_status))
        next.cpam_reimbursement_status = motifHasIjss(motif) ? "PENDING" : "NOT_APPLICABLE";
      if (motif.code !== THERAPEUTIC_MOTIF_CODE) next.therapeutic_rate = "";
      if (isNew && !prev.id_sample_leave && motif.category) {
        const types = workStoppageLeaveTypes.filter((sample) => sample.referenceCategory === motif.category);
        const type = types.find((sample) => normalizeLabel(sample.description) === normalizeLabel(motif.label)) || types[0];
        if (type) next.id_sample_leave = type.id;
      }
      return next;
    });
    setErrors((prev) => ({ ...prev, id_absence_reference: "", id_sample_leave: "" }));
  };

  const validate = () => {
    const next = {};
    if (!isEdit && !isNew && !form.id_planning) next.id_planning = Localize("WORK_STOPPAGE_REQUIRED");
    if (isNew && !form.id_collab) next.id_collab = Localize("WORK_STOPPAGE_REQUIRED");
    if (isNew && !form.id_sample_leave) next.id_sample_leave = Localize("WORK_STOPPAGE_REQUIRED");
    if (!form.id_absence_reference) next.id_absence_reference = Localize("WORK_STOPPAGE_REQUIRED");
    else if (form.id_absence_reference === CUSTOM_MOTIF && !form.custom_motif.trim())
      next.custom_motif = Localize("WORK_STOPPAGE_REQUIRED");
    // The period is the planning leave's: required, and non-empty, wherever it is set here.
    if (canEditPeriod) {
      if (!form.start_date) next.start_date = Localize("WORK_STOPPAGE_REQUIRED");
      if (!form.end_date) next.end_date = Localize("WORK_STOPPAGE_REQUIRED");
      // Also catches "Monday noon → Monday noon", which is not an absence.
      else if (form.start_date && !isLeavePeriodValid(form)) next.end_date = Localize("ADD_LEAVE_ERROR_DATE");
    }
    if (form.stoppage_status === "PROLONGATION" && !form.id_parent)
      next.id_parent = Localize("WORK_STOPPAGE_REQUIRED");
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  // The server refuses a save that lands on other absences until it is told to go ahead, and
  // answers with the plan instead. Holding it here lets the same submit run twice: once to
  // find out, once to confirm.
  const [overlapPlan, setOverlapPlan] = useState(null);

  const handleSubmit = async (confirmOverlaps = false) => {
    if (!validate()) return;
    setSaving(true);
    try {
      const body = {
        // One or the other: a référentiel line, or the motif typed by hand.
        id_absence_reference: form.id_absence_reference === CUSTOM_MOTIF ? null : form.id_absence_reference,
        custom_motif: form.id_absence_reference === CUSTOM_MOTIF ? form.custom_motif.trim() : null,
        stoppage_status: form.stoppage_status,
        id_parent: form.stoppage_status === "PROLONGATION" ? form.id_parent : null,
        received_at: orNull(form.received_at),
        declared_at: orNull(form.declared_at),
        subrogation: form.subrogation ? 1 : 0,
        salary_maintenance: form.salary_maintenance ? 1 : 0,
        cpam_days: orNull(form.cpam_days),
        therapeutic_rate: orNull(form.therapeutic_rate),
        maintenance_days_100: orNull(form.maintenance_days_100),
        maintenance_days_80: orNull(form.maintenance_days_80),
        maintenance_days_unpaid: orNull(form.maintenance_days_unpaid),
        cpam_reimbursement_status: form.cpam_reimbursement_status,
        prevoyance_reimbursement_status: form.prevoyance_reimbursement_status,
        comments: orNull(form.comments),
      };
      // The leave is only sent when declaring: on an edit it is the arrêt's identity. A new
      // arrêt sends what the API needs to create the planning leave instead.
      if (isNew) {
        body.create_leave = 1;
        body.id_collab = form.id_collab;
        body.id_sample_leave = form.id_sample_leave;
      } else if (!isEdit) {
        body.id_planning = form.id_planning;
      }
      // The period creates the leave (NEW) or moves it (edit); in EXISTING mode it is the
      // picked leave's and is not sent.
      if (canEditPeriod) {
        body.start_date = form.start_date;
        body.start_period = form.start_period;
        body.end_date = form.end_date;
        body.end_period = form.end_period;
      }

      // On an edit, a computed figure nobody touched is left out, so it stays computed (see
      // `baseline`). An already-manual figure is always sent — it is the user's own value.
      if (isEdit) {
        const untouched = (field) => String(form[field]) === String(baseline[field] ?? "");
        if (!overridden.cpam && untouched("cpam_days")) delete body.cpam_days;
        if (!overridden.maintenance && MAINTENANCE_FIELDS.every(untouched))
          MAINTENANCE_FIELDS.forEach((field) => delete body[field]);
      }

      if (confirmOverlaps) body.confirm_overlaps = true;

      const action = isEdit
        ? editWorkStoppage({ ...body, id: stoppage.id })
        : addWorkStoppage(body);
      const response = await dispatch(action);
      const data = response?.payload?.responseData;

      // Not an error: the arrêt lands on other absences and the server wants that acknowledged.
      // Nothing has been written, so backing out of the dialog leaves no trace.
      if (data && data.requires_confirmation) {
        setOverlapPlan(data.overlaps);
        return;
      }

      if (data && data.status === 1) {
        setOverlapPlan(null);
        showSuccessToast(Localize("WORK_STOPPAGE_SAVED"));
        // Justificatifs picked while declaring go onto the planning leave now that it exists.
        // The arrêt is saved either way; a file that fails is reported, not a reason to undo.
        if (!isEdit && pendingFiles.length && data.id_planning) {
          for (const pending of pendingFiles) {
            const upload = await dispatch(uploadWorkStoppageAttachment({ file: pending.file, idPlanning: data.id_planning }));
            if (upload?.payload?.responseData?.status !== 1)
              showErrorToast(`${Localize("WORK_STOPPAGE_ATTACHMENT_FAILED")} (${pending.file.name})`);
          }
        }
        onSaved();
        onHide();
      } else {
        showErrorToast(data?.message || data?.error || Localize("WORK_STOPPAGE_SAVE_FAILED"));
      }
    } catch (error) {
      showErrorToast(Localize("WORK_STOPPAGE_SAVE_FAILED"));
    } finally {
      setSaving(false);
    }
  };

  /**
   * Drop one manually entered figure back to the formula. Without it a figure typed once
   * could never be un-typed: every later save deliberately leaves it alone. Each flag resets
   * only its own figure, so the button sits on the row it concerns.
   *
   * The dialog stays open: the recomputed figure is fetched back and dropped into its field,
   * and anything else the user has typed but not yet saved is left as it was.
   */
  const recalculate = async (flags) => {
    setRecalculating(true);
    try {
      const response = await dispatch(editWorkStoppage({ id: stoppage.id, ...flags }));
      const data = response?.payload?.responseData;
      if (!data || data.status !== 1) {
        showErrorToast(data?.message || Localize("WORK_STOPPAGE_SAVE_FAILED"));
        return;
      }
      const fresh = (await dispatch(getWorkStoppage({ id: stoppage.id })))?.payload?.responseData?.stoppage;
      if (fresh) {
        setForm((prev) => ({
          ...prev,
          ...(flags.reset_cpam_days ? { cpam_days: fresh.cpam_days ?? "" } : {}),
          ...(flags.reset_maintenance ? {
            maintenance_days_100: fresh.maintenance_days_100 ?? "",
            maintenance_days_80: fresh.maintenance_days_80 ?? "",
            maintenance_days_unpaid: fresh.maintenance_days_unpaid ?? "",
          } : {}),
        }));
        setOverridden({
          cpam: !!fresh.cpam_days_overridden,
          maintenance: !!fresh.maintenance_overridden,
        });
        // The recomputed figures are the new baseline, so saving afterwards keeps them computed.
        setBaseline(computedFigures(fresh));
      }
      showSuccessToast(Localize("WORK_STOPPAGE_SAVED"));
      onSaved();
    } finally {
      setRecalculating(false);
    }
  };

  /** The recalculate button, shown only where the figure beside it was typed by hand. */
  const recalculateButton = (isOverridden, flags) => (isEdit && isOverridden ? (
    <Tooltip title={Localize("WORK_STOPPAGE_RECALCULATE_HELP")}>
      <Button
        size="small"
        variant="outlined"
        color="warning"
        startIcon={<RefreshRoundedIcon />}
        onClick={() => recalculate(flags)}
        disabled={recalculating}
        sx={{ flexShrink: 0, whiteSpace: "nowrap", borderRadius: "10px", textTransform: "none", fontWeight: 600 }}
      >
        <Langs str="WORK_STOPPAGE_RECALCULATE" />
      </Button>
    </Tooltip>
  ) : null);

  const leaveLabel = (leave) =>
    `${leave.prenom || ""} ${leave.nom || ""} — ${moment(Number(leave.from)).format("DD/MM/YYYY")}` +
    ` → ${moment(Number(leave.to)).format("DD/MM/YYYY")}` +
    `${leave.leave_label ? ` (${leave.leave_label})` : ""}` +
    `${leave.status === "PENDING" ? ` · ${Localize("WORK_STOPPAGE_PENDING_BADGE")}` : ""}`;

  // ── Justificatifs ──────────────────────────────────────────────────────────
  // On a declared arrêt they are on the server; while declaring they wait in `pendingFiles`.
  const attachmentItems = isEdit
    ? (attachments || []).map((attachment) => ({
      key: attachment.id,
      id: attachment.id,
      name: attachment.original_name,
      size: attachment.size,
      url: NetworkManager.getInstance().getWorkStoppageAttachmentUrl(attachment.id),
    }))
    : pendingFiles.map((pending) => ({ key: pending.key, name: pending.file.name, size: pending.file.size }));

  const addAttachment = async (file) => {
    if (!isEdit) {
      setPendingFiles((prev) => [...prev, { key: `${Date.now()}-${prev.length}`, file }]);
      return;
    }
    setUploading(true);
    try {
      const response = await dispatch(uploadWorkStoppageAttachment({ file, idPlanning: stoppage.id_planning }));
      if (response?.payload?.responseData?.status === 1) {
        showSuccessToast(Localize("WORK_STOPPAGE_ATTACHMENT_SAVED"));
        dispatch(getWorkStoppageAttachments(stoppage.id_planning));
      } else {
        showErrorToast(response?.payload?.responseData?.error || Localize("WORK_STOPPAGE_ATTACHMENT_FAILED"));
      }
    } finally {
      setUploading(false);
    }
  };

  const removeAttachment = async (item) => {
    if (!isEdit) {
      setPendingFiles((prev) => prev.filter((pending) => pending.key !== item.key));
      return;
    }
    const response = await dispatch(deleteWorkStoppageAttachment({ id: item.id }));
    if (response?.payload?.responseData?.status === 1) {
      showSuccessToast(Localize("WORK_STOPPAGE_ATTACHMENT_DELETED"));
      dispatch(getWorkStoppageAttachments(stoppage.id_planning));
    } else {
      showErrorToast(response?.payload?.responseData?.message || Localize("WORK_STOPPAGE_SAVE_FAILED"));
    }
  };

  /** A plain input of the form: small, full width, in the dialog's field style. */
  const field = (props) => <TextField fullWidth size="small" sx={formFieldSx} {...props} />;

  return (
    <FormDialog
      open={visible}
      onClose={onHide}
      title={<Langs str={isEdit ? "WORK_STOPPAGE_EDIT" : "WORK_STOPPAGE_ADD"} />}
      maxWidth="md"
      fullScreen={isMobile === 2}
      actions={(
        <>
          <FormButton variant="secondary" onClick={onHide}><Langs str="Cancel" /></FormButton>
          <FormButton onClick={() => handleSubmit(false)} disabled={saving}><Langs str="Save" /></FormButton>
        </>
      )}
    >
      {/* ── Mode: an absence already in the planning, or a new one ─────────── */}
      {!isEdit && (
        <Stack spacing={1} sx={{ mb: 1 }}>
          <Tabs
            value={mode}
            onChange={(_e, value) => { setMode(value); setErrors({}); }}
            variant="fullWidth"
            aria-label={Localize("WORK_STOPPAGE_MODE")}
            sx={{
              borderBottom: 1,
              borderColor: "divider",
              "& .MuiTab-root": { textTransform: "none", fontWeight: 600 },
              "& .Mui-selected": { color: (theme) => `${theme.palette.warning.main} !important`, fontWeight: 700 },
              "& .MuiTabs-indicator": { backgroundColor: (theme) => theme.palette.warning.main, height: 3 },
            }}
          >
            {/* Validating a request is by definition about that existing leave. */}
            <Tab value="NEW" label={Localize("WORK_STOPPAGE_MODE_NEW")} disabled={!!preselectedLeaveId} />
            <Tab value="EXISTING" label={Localize("WORK_STOPPAGE_MODE_EXISTING")} />
          </Tabs>
          {isNew && (
            <Typography variant="body2" sx={{ color: "rgba(0, 40, 128, 0.55)", px: 1 }}>
              <Langs str="WORK_STOPPAGE_MODE_NEW_HELP" />
            </Typography>
          )}
        </Stack>
      )}

      {/* ── NEW: who, and which leave type ─────────────────────────────────── */}
      {isNew && (
        <>
          <FormRow label={<Langs str="WORK_STOPPAGE_COLLAB" />} required>
            <Autocomplete
              fullWidth
              size="small"
              options={collabOptions}
              value={selectedCollab}
              onChange={(_e, value) => { setField("id_collab", value ? value.id : null); setField("id_parent", null); }}
              // The team is part of the label so typing a team name finds its collabs too.
              getOptionLabel={(collab) => `${collab.prenom || ""} ${collab.nom || ""}`.trim()
                + (collab.current_team_name ? ` — ${collab.current_team_name}` : "")}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderOption={(props, collab) => (
                <li {...props} key={collab.id}>
                  <Stack spacing={0}>
                    <span>{`${collab.prenom || ""} ${collab.nom || ""}`.trim()}</span>
                    {collab.current_team_name && (
                      <Typography variant="caption" color="text.secondary">{collab.current_team_name}</Typography>
                    )}
                  </Stack>
                </li>
              )}
              renderInput={(params) => (
                <TextField {...params} sx={formFieldSx} error={!!errors.id_collab} helperText={errors.id_collab} />
              )}
            />
          </FormRow>
          <FormRow label={<Langs str="WORK_STOPPAGE_LEAVE_TYPE" />} required>
            <LeaveTypeSelect
              value={form.id_sample_leave}
              onChange={onLeaveTypeChange}
              options={workStoppageLeaveTypes}
              error={!!errors.id_sample_leave}
              helperText={errors.id_sample_leave}
            />
          </FormRow>
        </>
      )}

      {/* ── EXISTING: the absence ──────────────────────────────────────────── */}
      {!isEdit && !isNew && (
        <FormRow label={<Langs str="WORK_STOPPAGE_SELECT_LEAVE" />} required>
          <Autocomplete
            fullWidth
            size="small"
            options={eligibleLeaves || []}
            loading={loadingLeaves}
            value={selectedLeave}
            onChange={(_e, value) => onLeaveChange(value)}
            getOptionLabel={leaveLabel}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            noOptionsText={Localize("WORK_STOPPAGE_NO_ELIGIBLE_LEAVES")}
            renderInput={(params) => (
              <TextField
                {...params}
                sx={formFieldSx}
                error={!!errors.id_planning}
                helperText={errors.id_planning || Localize("WORK_STOPPAGE_LEAVE_HELP")}
              />
            )}
          />
        </FormRow>
      )}
      {isEdit && (
        <Alert severity="info" sx={{ borderRadius: "12px", mb: 1 }}>
          {`${stoppage.prenom || ""} ${stoppage.nom || ""} — ${Localize("WORK_STOPPAGE_LEAVE")}: `}
          {stoppage.id_planning
            ? `#${stoppage.id_planning}${stoppage.planning_leave_label ? ` (${stoppage.planning_leave_label})` : ""}`
            : "—"}
        </Alert>
      )}

      {/* The leave type of an absence already in the planning — shown, not edited here. */}
      {!isNew && (isEdit || selectedLeave) && (
        <FormRow label={<Langs str="WORK_STOPPAGE_LEAVE_TYPE" />}>
          {field({ value: leaveTypeLabel || "—", InputProps: { readOnly: true }, disabled: true })}
        </FormRow>
      )}

      {/* ── Motif — grouped by référentiel category, the leave type's first ──── */}
      <FormRow label={<Langs str="WORK_STOPPAGE_MOTIF" />} required>
        {field({
          select: true,
          value: form.id_absence_reference,
          onChange: (e) => onMotifChange(e.target.value),
          error: !!errors.id_absence_reference,
          helperText: errors.id_absence_reference,
          SelectProps: { MenuProps: { PaperProps: { sx: { maxHeight: 420 } } } },
          children: [
            ...motifGroups.flatMap(([category, rows]) => [
              <ListSubheader key={`category-${category}`} sx={{ fontWeight: 700, lineHeight: "32px", color: "text.secondary" }}>
                {category}
              </ListSubheader>,
              ...rows.map((row) => (
                <MenuItem key={row.id} value={row.id} sx={{ whiteSpace: "normal", pl: 3 }}>{row.label}</MenuItem>
              )),
            ]),
            <MenuItem key={CUSTOM_MOTIF} value={CUSTOM_MOTIF} sx={{ fontStyle: "italic", borderTop: 1, borderColor: "divider" }}>
              {Localize("WORK_STOPPAGE_CUSTOM_MOTIF")}
            </MenuItem>,
          ],
        })}
      </FormRow>
      {form.id_absence_reference === CUSTOM_MOTIF && (
        <FormRow label={<Langs str="WORK_STOPPAGE_CUSTOM_MOTIF_LABEL" />} required help={Localize("WORK_STOPPAGE_CUSTOM_MOTIF_HELP")}>
          {field({
            value: form.custom_motif,
            onChange: (e) => setField("custom_motif", e.target.value),
            error: !!errors.custom_motif,
            helperText: errors.custom_motif,
            inputProps: { maxLength: 255 },
            autoFocus: true,
          })}
        </FormRow>
      )}
      {selectedMotif && (
        <FormRow label="">
          {/* What the grid says about the motif picked — the values the inputs below took. */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "auto 1fr" },
              columnGap: 2,
              rowGap: 0.5,
              px: 1.5,
              py: 1,
              borderRadius: "10px",
              bgcolor: "rgba(255, 144, 21, 0.06)",
              border: "1px solid rgba(255, 144, 21, 0.2)",
            }}
          >
            {[
              ["WORK_STOPPAGE_REF_CATEGORY", selectedMotif.category],
              ["WORK_STOPPAGE_REF_DURATION", selectedMotif.duration_rule],
              ["WORK_STOPPAGE_REF_CONDITIONS", selectedMotif.conditions],
              ["WORK_STOPPAGE_REF_IJSS", selectedMotif.ijss],
              ["WORK_STOPPAGE_REF_JUSTIFICATIF", selectedMotif.justificatif],
              ["WORK_STOPPAGE_CARENCE_DAYS", selectedMotif.carence_days],
            ].filter(([, value]) => value !== null && typeof value !== "undefined" && String(value).trim() !== "")
              .map(([labelKey, value]) => (
                <React.Fragment key={labelKey}>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: "text.secondary" }}>
                    <Langs str={labelKey} />
                  </Typography>
                  <Typography variant="caption" sx={{ color: "text.primary" }}>{String(value)}</Typography>
                </React.Fragment>
              ))}
          </Box>
        </FormRow>
      )}

      {/* ── Justificatifs — on a declared arrêt only when it has a planning leave ── */}
      {(!isEdit || stoppage.id_planning) && (
        <FormRow label={<Langs str="WORK_STOPPAGE_JUSTIFICATIFS" />} alignTop>
          <WorkStoppageAttachments
            items={attachmentItems}
            loading={isEdit && attachmentsLoading}
            busy={uploading}
            canEdit
            onAdd={addAttachment}
            onRemove={removeAttachment}
            titleKey={null}
          />
        </FormRow>
      )}

      {/* ── Statut ──────────────────────────────────────────────────────────── */}
      <FormRow label={<Langs str="WORK_STOPPAGE_STATUS" />}>
        {field({
          select: true,
          value: form.stoppage_status,
          onChange: (e) => setField("stoppage_status", e.target.value),
          children: [
            <MenuItem key="INITIAL" value="INITIAL"><StatusOption status="INITIAL" /></MenuItem>,
            <MenuItem key="PROLONGATION" value="PROLONGATION"><StatusOption status="PROLONGATION" /></MenuItem>,
          ],
        })}
      </FormRow>

      {form.stoppage_status === "PROLONGATION" && (
        <FormRow label={<Langs str="WORK_STOPPAGE_PARENT" />} required>
          {field({
            select: true,
            value: form.id_parent || "",
            onChange: (e) => setField("id_parent", e.target.value),
            error: !!errors.id_parent,
            helperText: errors.id_parent || Localize("WORK_STOPPAGE_PARENT_HELP"),
            children: parentOptions.map((row) => (
              <MenuItem key={row.id} value={row.id}>
                {`#${row.id} — ${moment(row.start_date).format("DD/MM/YYYY")}`}
                {row.end_date ? ` → ${moment(row.end_date).format("DD/MM/YYYY")}` : ""}
              </MenuItem>
            )),
          })}
        </FormRow>
      )}

      {/* ── Dates — the planning leave's: set here when new, moved here on an edit ── */}
      <LeaveDateRangeField
        value={form}
        onChange={(next) => {
          setForm((prev) => ({ ...prev, ...next }));
          setErrors((prev) => ({ ...prev, start_date: "", end_date: "" }));
        }}
        errors={errors}
        required={canEditPeriod}
        disabled={!canEditPeriod}
      />
      <FormRow label={<Langs str="WORK_STOPPAGE_RECEIVED_AT" />}>
        <FormDatePicker clearable value={form.received_at} onChange={(date) => setField("received_at", date)} />
      </FormRow>
      <FormRow label={<Langs str="WORK_STOPPAGE_DECLARED_AT" />}>
        <FormDatePicker clearable value={form.declared_at} onChange={(date) => setField("declared_at", date)} />
      </FormRow>

      {/* ── Droits ──────────────────────────────────────────────────────────── */}
      <FormRow label={<Langs str="WORK_STOPPAGE_SUBROGATION" />}>
        <Switch
          color="warning"
          checked={!!form.subrogation}
          onChange={(e) => setField("subrogation", e.target.checked ? 1 : 0)}
        />
      </FormRow>
      <FormRow label={<Langs str="WORK_STOPPAGE_SALARY_MAINTENANCE" />}>
        <Switch
          color="warning"
          checked={!!form.salary_maintenance}
          onChange={(e) => setField("salary_maintenance", e.target.checked ? 1 : 0)}
        />
      </FormRow>

      {/* ── Indemnisation ───────────────────────────────────────────────────── */}
      <FormRow label={<Langs str="WORK_STOPPAGE_CPAM_DAYS" />} help={Localize("WORK_STOPPAGE_COMPUTED_HELP")}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          {field({
            type: "number",
            value: form.cpam_days,
            onChange: (e) => setField("cpam_days", e.target.value),
            inputProps: { min: 0 },
          })}
          {recalculateButton(overridden.cpam, { reset_cpam_days: true })}
        </Stack>
      </FormRow>
      {/* Only meaningful for temps partiel thérapeutique; still shown if a rate is on file. */}
      {(selectedMotif?.code === THERAPEUTIC_MOTIF_CODE || form.therapeutic_rate !== "") && (
      <FormRow label={<Langs str="WORK_STOPPAGE_THERAPEUTIC_RATE" />}>
        {field({
          type: "number",
          value: form.therapeutic_rate,
          onChange: (e) => setField("therapeutic_rate", e.target.value),
          InputProps: { endAdornment: <InputAdornment position="end">%</InputAdornment> },
          inputProps: { min: 0, max: 100 },
        })}
      </FormRow>
      )}

      {/* ── Maintien de salaire (Syntec) ──────────────────────────────────────
          Left empty, the three fields are computed from the convention brackets, the
          collab's statut and their ancienneté — including a seniority threshold crossed
          mid-arrêt. Filling any ONE of them marks the whole split as manual, so a figure
          already used on a payslip is never recomputed underneath Paie. */}
      <FormSection><Langs str="WORK_STOPPAGE_MAINTENANCE" /></FormSection>
      {/* The three maintien figures are overridden together, so their one recalculate
          button sits on the first of them. */}
      <FormRow label={<Langs str="WORK_STOPPAGE_MAINTENANCE_100" />} help={Localize("WORK_STOPPAGE_MAINTENANCE_HELP")}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          {field({
            type: "number",
            value: form.maintenance_days_100,
            onChange: (e) => setField("maintenance_days_100", e.target.value),
            inputProps: { min: 0 },
          })}
          {recalculateButton(overridden.maintenance, { reset_maintenance: true })}
        </Stack>
      </FormRow>
      <FormRow label={<Langs str="WORK_STOPPAGE_MAINTENANCE_REDUCED" />}>
        {field({
          type: "number",
          value: form.maintenance_days_80,
          onChange: (e) => setField("maintenance_days_80", e.target.value),
          inputProps: { min: 0 },
        })}
      </FormRow>
      <FormRow label={<Langs str="WORK_STOPPAGE_MAINTENANCE_UNPAID" />}>
        {field({
          type: "number",
          value: form.maintenance_days_unpaid,
          onChange: (e) => setField("maintenance_days_unpaid", e.target.value),
          inputProps: { min: 0 },
        })}
      </FormRow>

      {/* ── Statut de remboursement ─────────────────────────────────────────── */}
      <FormSection><Langs str="WORK_STOPPAGE_REIMBURSEMENT" /></FormSection>
      <FormRow label={<Langs str="WORK_STOPPAGE_CPAM_REIMBURSEMENT_STATUS" />} help={Localize("WORK_STOPPAGE_REIMBURSEMENT_HELP")}>
        {field({
          select: true,
          value: form.cpam_reimbursement_status,
          onChange: (e) => setField("cpam_reimbursement_status", e.target.value),
          children: REIMBURSEMENT_STATUSES.map((status) => (
            <MenuItem key={status} value={status}><StatusOption status={status} /></MenuItem>
          )),
        })}
      </FormRow>
      <FormRow label={<Langs str="WORK_STOPPAGE_PREVOYANCE_REIMBURSEMENT_STATUS" />}>
        {field({
          select: true,
          value: form.prevoyance_reimbursement_status,
          onChange: (e) => setField("prevoyance_reimbursement_status", e.target.value),
          children: REIMBURSEMENT_STATUSES.map((status) => (
            <MenuItem key={status} value={status}><StatusOption status={status} /></MenuItem>
          )),
        })}
      </FormRow>

      <FormRow label={<Langs str="Comments" />} alignTop>
        {field({
          multiline: true,
          minRows: 3,
          value: form.comments,
          onChange: (e) => setField("comments", e.target.value),
        })}
      </FormRow>

      {/* Nothing has been written when this is showing: the server answered with the plan
          instead of saving, and confirming re-runs the same submit with the go-ahead.

          The dialog is deliberately not closed on confirm. `handleSubmit` closes it once the
          save comes back (and leaves it open if it fails), which is what makes its `saving`
          prop mean anything: on a phone the form's own Save button is off-screen behind this
          dialog, so closing first left a second or two where the tap had visibly done
          nothing at all. */}
      <WorkStoppageOverlapDialog
        open={!!overlapPlan}
        overlaps={overlapPlan}
        saving={saving}
        onCancel={() => setOverlapPlan(null)}
        onConfirm={() => handleSubmit(true)}
      />
    </FormDialog>
  );
}
